import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Database,
  Plus,
  Trash2,
  Download,
  RefreshCw,
  ArrowLeft,
  HardDrive,
  Calendar,
  BarChart3,
  CheckCircle,
  Wifi,
  WifiOff,
  AlertTriangle,
  Calculator
} from 'lucide-react';
import { FileUpload } from './FileUpload';
import { StockData } from '../types';
import { 
  getDatabaseList, 
  loadDatabase, 
  deleteDatabase, 
  mergeDataIntoDatabase,
  getDatabaseStorageInfo,
  DatabaseMetadata,
  clearDatabaseCache
} from '../utils/optimizedDatabaseManager';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { ErrorBoundary } from './ErrorBoundary';
import { trackRenderTime, trackComponentLifecycle } from '../utils/performance';

interface DatabaseManagementProps {
  currentDatabaseName: string | null;
  onDatabaseSelected: (data: StockData[], databaseName: string) => void;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
}

export const DatabaseManagement: React.FC<DatabaseManagementProps> = ({ 
  currentDatabaseName,
  onDatabaseSelected, 
  onError,
  onSuccess
}) => {
  const navigate = useNavigate();
  const [databases, setDatabases] = useState<DatabaseMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(true); // Inizia con true
  const [isConnected, setIsConnected] = useState(true); // Nuovo stato per la connessione
  const [showMergeUpload, setShowMergeUpload] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [storageInfo, setStorageInfo] = useState({
    totalDatabases: 0,
    totalRecords: 0,
    totalSizeBytes: 0,
    totalSizeMB: '∞',
    totalLocalStorageUsed: '0.00',
    storageUsagePercentage: '0.0',
    isNearLimit: false
  });
  
  // Track component render time
  const finishRenderTracking = trackRenderTime('DatabaseManagement');
  
  // Track component lifecycle
  useEffect(() => {
    return trackComponentLifecycle('DatabaseManagement');
  }, []);

  useEffect(() => {
    loadDatabaseList();
    loadStorageInfo();
  }, []);

  const loadDatabaseList = async () => {
    setIsLoading(true);
    try {
      console.log('Tentativo di caricamento lista database...');
      const dbList = await getDatabaseList();
      console.log('Lista database caricata con successo:', dbList.length, 'database trovati');
      setDatabases(dbList);
      setIsConnected(true);
    } catch (error) {
      console.error('Errore nel caricamento della lista database:', error);
      setIsConnected(false);
      onError('Errore di connessione al database. Verifica la configurazione Supabase.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadStorageInfo = async () => {
    try {
      const info = await getDatabaseStorageInfo();
      setStorageInfo(info);
    } catch (error) {
      console.error('Errore nel caricamento delle informazioni storage:', error);
    }
  };

  const handleLoadDatabase = async (databaseName: string) => {
    setIsLoading(true);
    try {
      const data = await loadDatabase(databaseName);
      if (data) {
        onDatabaseSelected(data, databaseName);
        onSuccess(`Database "${databaseName}" caricato con successo`);
        navigate('/dashboard');
      } else {
        onError(`Impossibile caricare il database "${databaseName}"`);
      }
    } catch (error) {
      console.error('Errore nel caricamento del database:', error);
      onError(`Errore nel caricamento del database: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDatabase = async (databaseName: string) => {
    setIsLoading(true);
    try {
      const success = await deleteDatabase(databaseName);
      if (success) {
        onSuccess(`Database "${databaseName}" eliminato con successo`);
        loadDatabaseList();
        loadStorageInfo();
        
        // Clear cache for this database
        clearDatabaseCache(databaseName);
      } else {
        onError(`Errore nell'eliminazione del database "${databaseName}"`);
      }
    } catch (error) {
      console.error('Errore nell\'eliminazione del database:', error);
      onError(`Errore nell'eliminazione del database: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`);
    } finally {
      setIsLoading(false);
      setShowDeleteConfirm(null);
    }
  };

  const handleMergeData = async (databaseName: string, newData: StockData[]) => {
    setIsLoading(true);
    try {
      const success = await mergeDataIntoDatabase(databaseName, newData);
      if (success) {
        onSuccess(`${newData.length} nuovi record aggiunti al database "${databaseName}"`);
        loadDatabaseList();
        loadStorageInfo();
        
        // Clear cache for this database
        clearDatabaseCache(databaseName);
      } else {
        onError(`Errore nell'unione dei dati al database "${databaseName}"`);
      }
    } catch (error) {
      console.error('Errore nell\'unione dei dati:', error);
      if (error instanceof Error) {
        onError(error.message);
      } else {
        onError(`Errore nell'unione dei dati: ${error}`);
      }
    } finally {
      setIsLoading(false);
      setShowMergeUpload(null);
    }
  };

  const handleExportDatabase = async (databaseName: string) => {
    try {
      const data = await loadDatabase(databaseName);
      if (!data) {
        onError(`Impossibile esportare il database "${databaseName}"`);
        return;
      }

      // Converti in CSV
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(','),
        ...data.map(row => 
          headers.map(header => {
            const value = (row as any)[header];
            if (value instanceof Date) {
              return value.toISOString();
            }
            return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
          }).join(',')
        )
      ].join('\n');

      // Download del file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${databaseName}_export.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      onSuccess(`Database "${databaseName}" esportato con successo`);
    } catch (error) {
      console.error('Errore nell\'esportazione del database:', error);
      onError(`Errore nell'esportazione del database: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`);
    }
  };

  const formatFileSize = (records: number) => {
    if (records < 1000) return `${records} record`;
    if (records < 1000000) return `${(records / 1000).toFixed(1)}K record`;
    return `${(records / 1000000).toFixed(1)}M record`;
  };
  
  // Track render time at the end of the component
  useEffect(() => {
    return () => {
      finishRenderTracking();
    };
  }, [finishRenderTracking]);

  // Mostra stato di connessione se non connesso
  if (!isConnected) {
    return (
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="p-3 bg-red-400/10 rounded-xl">
              <WifiOff className="h-6 w-6 sm:h-8 sm:w-8 text-red-400" />
            </div>
            <h1 className="text-2xl sm:text-4xl font-bold text-white">Connessione Database Non Disponibile</h1>
          </div>
          <p className="text-base sm:text-xl text-gray-400 max-w-2xl mx-auto mb-8">
            Impossibile connettersi al database Supabase. Verifica la configurazione.
          </p>
          
          <div className="max-w-2xl mx-auto bg-gray-800/50 border border-gray-700 rounded-lg p-4 sm:p-6 text-left mb-8">
            <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Configurazione Richiesta:</h3>
            <ol className="text-gray-300 space-y-2 text-xs sm:text-sm">
              <li>1. Crea un progetto su <a href="https://supabase.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">supabase.com</a></li>
              <li>2. Vai su Settings → API e copia l'URL del progetto e la chiave anon</li>
              <li>3. Crea un file <code className="bg-gray-700 px-2 py-1 rounded">.env</code> nella root del progetto</li>
              <li>4. Aggiungi le variabili:
                <pre className="bg-gray-900 p-3 rounded mt-2 text-xs overflow-x-auto">
{`VITE_SUPABASE_URL=your_supabase_project_url
VITE_SUPABASE_ANON_KEY=your_supabase_anon_key`}
                </pre>
              </li>
              <li>5. Vai su SQL Editor in Supabase e esegui il contenuto del file <code className="bg-gray-700 px-2 py-1 rounded">supabase/migrations/20250710153540_blue_waterfall.sql</code></li>
              <li>6. Verifica che le tabelle <code className="bg-gray-700 px-2 py-1 rounded">databases</code> e <code className="bg-gray-700 px-2 py-1 rounded">stock_data</code> siano state create</li>
            </ol>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={loadDatabaseList}
              className="px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors text-sm sm:text-base"
              disabled={isLoading}
            >
              {isLoading ? (
                <RefreshCw className="h-4 w-4 animate-spin mx-auto" />
              ) : (
                'Riprova Connessione'
              )}
            </button>
            
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm sm:text-base"
            >
              Torna alla Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showMergeUpload) {
    return (
      <ErrorBoundary>
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="text-center mb-6 sm:mb-8">
            <div className="flex items-center justify-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
              <div className="p-2 sm:p-3 bg-green-400/10 rounded-xl">
                <Plus className="h-6 w-6 sm:h-8 sm:w-8 text-green-400" />
              </div>
              <h1 className="text-2xl sm:text-4xl font-bold text-white">Unisci Dati</h1>
            </div>
            <p className="text-base sm:text-xl text-gray-400 max-w-2xl mx-auto">
              Aggiungi nuovi dati al database "{showMergeUpload}"
            </p>
          </div>

          {/* Indicatore Connessione */}
          <div className="max-w-2xl mx-auto mb-6">
            <div className="bg-green-400/10 border border-green-400/30 rounded-lg p-3 sm:p-4">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <Wifi className="h-4 w-4 sm:h-5 sm:w-5 text-green-400 flex-shrink-0" />
                <div>
                  <h4 className="text-sm sm:text-base font-medium text-green-400">Database Cloud Attivo</h4>
                  <p className="text-xs sm:text-sm text-green-300/80 mt-1">
                    Connesso a Supabase - archiviazione sicura e illimitata
                  </p>
                </div>
              </div>
            </div>
          </div>

          <FileUpload 
            onDataUpload={(data) => handleMergeData(showMergeUpload, data)}
            onError={onError}
            isLoading={isLoading}
          />

          <div className="text-center mt-6 sm:mt-8">
            <button
              onClick={() => setShowMergeUpload(null)}
              className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm sm:text-base"
              disabled={isLoading}
            >
              ← Annulla
            </button>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 space-y-4 sm:space-y-0">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Torna alla Dashboard</span>
            </button>
            <div className="hidden sm:block h-6 w-px bg-gray-600"></div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="p-2 bg-blue-400/10 rounded-lg">
                <Database className="h-5 w-5 sm:h-6 sm:w-6 text-blue-400" />
              </div>
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-white">Gestione Database</h1>
                <p className="text-xs sm:text-sm text-gray-400">Amministra i tuoi database di gap trading</p>
              </div>
            </div>
          </div>

          <button
            onClick={() => navigate('/backtesting')}
            className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg transition-colors text-sm"
          >
            <Calculator className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Backtester</span>
          </button>
        </div>

        {/* Indicatore Connessione Cloud */}
        <div className="mb-6 sm:mb-8">
          <div className="bg-green-400/10 border border-green-400/30 rounded-lg p-4 sm:p-6">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <Wifi className="h-5 w-5 sm:h-6 sm:w-6 text-green-400 flex-shrink-0" />
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-green-400">Database Cloud Supabase Attivo</h3>
                <p className="text-xs sm:text-sm text-green-300/80 mt-2">
                  Connesso al database cloud - archiviazione sicura, illimitata e accessibile da qualsiasi dispositivo.
                  I tuoi dati sono protetti e sincronizzati automaticamente.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Statistiche Storage */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="p-1.5 sm:p-2 bg-blue-400/10 rounded-lg">
                <Database className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-white">{storageInfo.totalDatabases}</p>
                <p className="text-xs sm:text-sm text-gray-400">Database Totali</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="p-1.5 sm:p-2 bg-green-400/10 rounded-lg">
                <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-white">{storageInfo.totalRecords.toLocaleString()}</p>
                <p className="text-xs sm:text-sm text-gray-400">Record Totali</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="p-1.5 sm:p-2 bg-purple-400/10 rounded-lg">
                <HardDrive className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-white">∞</p>
                <p className="text-xs sm:text-sm text-gray-400">Spazio Disponibile</p>
              </div>
            </div>
          </div>
        </div>

        {/* Database Attivo */}
        {currentDatabaseName && (
          <div className="bg-green-400/10 border border-green-400/30 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-400" />
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-white">Database Attualmente Caricato</h3>
                <p className="text-sm sm:text-base text-green-400 font-medium">{currentDatabaseName}</p>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="bg-blue-400/10 border border-blue-400/30 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <RefreshCw className="h-5 w-5 sm:h-6 sm:w-6 text-blue-400 animate-spin" />
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-white">Caricamento in corso...</h3>
                <p className="text-xs sm:text-sm text-blue-300/80 mt-1">
                  Connessione al database e recupero delle informazioni
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Lista Database */}
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg overflow-hidden">
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-700">
            <h3 className="text-base sm:text-lg font-semibold text-white">Database Salvati</h3>
            <p className="text-xs sm:text-sm text-gray-400 mt-1">
              {databases.length} database disponibili
            </p>
          </div>

          {databases.length === 0 && !isLoading ? (
            <div className="p-8 sm:p-12 text-center">
              <div className="p-3 sm:p-4 bg-gray-700/30 rounded-xl inline-block mb-3 sm:mb-4">
                <Database className="h-6 w-6 sm:h-8 sm:w-8 text-gray-500" />
              </div>
              <h4 className="text-base sm:text-lg font-medium text-white mb-2">Nessun Database Trovato</h4>
              <p className="text-xs sm:text-sm text-gray-400 mb-4 sm:mb-6">Torna alla dashboard principale per caricare il tuo primo database</p>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors text-sm sm:text-base"
              >
                Vai alla Dashboard
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-700">
              {databases.map((db) => (
                <div key={db.id} className="p-4 sm:p-6 hover:bg-gray-800/30 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h4 className="text-base sm:text-lg font-medium text-white">{db.name}</h4>
                        {currentDatabaseName === db.name && (
                          <span className="px-2 py-1 bg-green-400/10 text-green-400 text-xs rounded-full border border-green-400/30">
                            Attivo
                          </span>
                        )}
                      </div>
                      
                      {db.description && (
                        <p className="text-xs sm:text-sm text-gray-400 mb-3">{db.description}</p>
                      )}
                      
                      <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-xs text-gray-500">
                        <div className="flex items-center space-x-1 sm:space-x-2">
                          <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span>{formatFileSize(db.recordCount)}</span>
                        </div>
                        <div className="flex items-center space-x-1 sm:space-x-2">
                          <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span>Creato: {format(db.createdAt, 'dd/MM/yyyy', { locale: it })}</span>
                        </div>
                        <div className="flex items-center space-x-1 sm:space-x-2">
                          <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span>Aggiornato: {format(db.lastModified, 'dd/MM/yyyy', { locale: it })}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 sm:ml-6">
                      <button
                        onClick={() => handleLoadDatabase(db.name)}
                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors text-xs sm:text-sm font-medium"
                        disabled={isLoading}
                        title="Carica questo database"
                      >
                        Carica
                      </button>
                      
                      <button
                        onClick={() => setShowMergeUpload(db.name)}
                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors text-xs sm:text-sm font-medium"
                        disabled={isLoading}
                        title="Aggiungi nuovi dati a questo database"
                      >
                        <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                      </button>
                      
                      <button
                        onClick={() => handleExportDatabase(db.name)}
                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg transition-colors text-xs sm:text-sm font-medium"
                        disabled={isLoading}
                        title="Esporta database come CSV"
                      >
                        <Download className="h-3 w-3 sm:h-4 sm:w-4" />
                      </button>
                      
                      <button
                        onClick={() => setShowDeleteConfirm(db.name)}
                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors text-xs sm:text-sm font-medium"
                        disabled={isLoading}
                        title="Elimina questo database"
                      >
                        <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modal Conferma Eliminazione */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 sm:p-6 max-w-md w-full mx-auto">
              <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
                <Trash2 className="h-5 w-5 sm:h-6 sm:w-6 text-red-400" />
                <h3 className="text-base sm:text-lg font-semibold text-white">Conferma Eliminazione</h3>
              </div>
              
              <p className="text-xs sm:text-sm text-gray-400 mb-4 sm:mb-6">
                Sei sicuro di voler eliminare il database "{showDeleteConfirm}"? 
                Questa azione non può essere annullata e tutti i dati verranno eliminati permanentemente.
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => handleDeleteDatabase(showDeleteConfirm)}
                  className="flex-1 px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors font-medium text-sm"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <RefreshCw className="h-4 w-4 animate-spin mx-auto" />
                  ) : (
                    'Elimina'
                  )}
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 px-3 sm:px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors font-medium text-sm"
                  disabled={isLoading}
                >
                  Annulla
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};